import { Spinner } from '@blueprintjs/core'
import classNames from 'classnames'
import numeral from 'numeral'
import React from 'react'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { JSONElementFactory } from 'browser/components/json-elements/higher-order-components/json-element-factory'
import { resolveRefreshInterval, toRefreshMs } from './metric-refresh'
import { useMetricData } from './use-metric-data'

interface IUIMetricProps extends IBaseProps {
  /** PromQL; must reduce to a single number once summed. */
  query?: string
  /** Only 'stat' today; other charts render through ui:metricChart. */
  display?: 'stat'
  title?: string
  /** numeral format string, default '0,0'. */
  format?: string
  unit?: string
  /** Re-query interval: '30s', '5m', '1h'; '0s' disables. Defaults to '5m'. */
  refreshInterval?: string
  // Injected by JSONElementFactory; declared so the factory's prop constraint fits.
  label?: unknown
  frames?: unknown
  errors?: unknown
  value?: unknown
  focusHotkey?: unknown
}

/** Display-only stat tile for a per-firm metric, authored inline on the uiSchema node. */
const UIMetricComponent: React.FC<IUIMetricProps> = (props) => {
  const { query, unit, title, format, className, refreshInterval } = props
  const { isLoading, error, isEmpty, stat } = useMetricData(
    query,
    toRefreshMs(resolveRefreshInterval(refreshInterval))
  )

  return (
    <div className={classNames('c-dashboardTile pa3', className)}>
      {title && <h5 className="c-dashboardTile-title">{title}</h5>}
      <div className="u-positionRelative">{renderBody()}</div>
    </div>
  )

  function renderBody() {
    if (!query) {
      return null
    }
    if (isLoading) {
      return <Spinner size={20} />
    }
    if (error) {
      return <div className="c-dashboardTile-error u-textColorError">{error}</div>
    }
    if (isEmpty || stat === undefined) {
      return <div className="c-dashboardTile-empty u-textColorMuted">No data for this period</div>
    }
    return (
      <h1 className="c-dashboardTile-value f1">
        {numeral(stat).format(format ?? '0,0')}
        {unit && <span className="c-dashboardTile-unit f4 ml2">{unit}</span>}
      </h1>
    )
  }
}

export const UIMetric = JSONElementFactory(UIMetricComponent)
